import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, User, Eye, EyeOff, ArrowRight, LogIn } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MemberAuth = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const texts = {
    de: { login: 'Anmelden', register: 'Registrieren', name: 'Vollstaendiger Name', email: 'E-Mail-Adresse', password: 'Passwort', noAccount: 'Noch kein Konto?', hasAccount: 'Bereits ein Konto?', welcome: 'Mitgliederbereich', subtitle: 'Melden Sie sich an, um exklusive Gemeinde-Inhalte zu sehen.', error401: 'E-Mail oder Passwort falsch', error409: 'E-Mail-Adresse bereits registriert', passwordHint: 'Mindestens 8 Zeichen' },
    en: { login: 'Login', register: 'Register', name: 'Full Name', email: 'Email Address', password: 'Password', noAccount: 'No account yet?', hasAccount: 'Already have an account?', welcome: 'Members Area', subtitle: 'Sign in to access exclusive community content.', error401: 'Wrong email or password', error409: 'Email already registered', passwordHint: 'At least 8 characters' },
    fr: { login: 'Connexion', register: 'Inscription', name: 'Nom complet', email: 'Adresse e-mail', password: 'Mot de passe', noAccount: 'Pas encore de compte?', hasAccount: 'Deja un compte?', welcome: 'Espace membres', subtitle: 'Connectez-vous pour voir le contenu exclusif.', error401: 'E-mail ou mot de passe incorrect', error409: 'E-mail deja enregistre', passwordHint: 'Au moins 8 caracteres' },
  };
  const tx = texts[lang] || texts.de;

  useEffect(() => {
    const token = localStorage.getItem('member_token');
    if (token) navigate('/mitglieder');
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/members/login' : '/api/members/register';
      const body = mode === 'login'
        ? { email, password }
        : { name, email, password };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) setError(tx.error401);
        else if (res.status === 409) setError(tx.error409);
        else setError(data.detail || 'Fehler');
        return;
      }

      localStorage.setItem('member_token', data.access_token);
      localStorage.setItem('member_name', data.name);
      localStorage.setItem('member_email', data.email);
      navigate('/mitglieder');
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f2440] via-[#1a3a5c] to-[#0f2440] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{tx.welcome}</h1>
          <p className="text-white/60 text-sm">{tx.subtitle}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              data-testid="member-auth-login-tab"
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
                mode === 'login' ? 'bg-white shadow text-[#0f2440]' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tx.login}
            </button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              data-testid="member-auth-register-tab"
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
                mode === 'register' ? 'bg-white shadow text-[#0f2440]' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tx.register}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tx.name}</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    data-testid="member-name-input"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx.email}</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  data-testid="member-email-input"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx.password}</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="member-password-input"
                  className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0f2440]/30 focus:border-[#0f2440]"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === 'register' && (
                <p className="text-xs text-gray-400 mt-1">{tx.passwordHint}</p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium" data-testid="member-auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="member-auth-submit"
              className="w-full py-3 bg-[#0f2440] text-white rounded-lg font-semibold hover:bg-[#1a3a5c] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {mode === 'login' ? <LogIn className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  {mode === 'login' ? tx.login : tx.register}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-400 hover:text-[#0f2440] transition-colors">
              Zurueck zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberAuth;
